<style lang="scss" src="./login.scss"></style>

<template>
  <a-modal
    v-model:open="isOpen"
    class="login-modal"
    :closable="true"
    :mask-closable="false"
    :keyboard="false"
    :footer="null"
    @cancel="close"
    centered
    wrap-class-name="full-modal-login"
  >
    <template #title>
      <div class="login-modal__header">
        <span class="login-modal__title">{{ modalTitle }}</span>
        <span v-if="isRecoverMode" class="login-modal__description">
          Укажите почтовый адрес, на который выслать ссылку для изменения пароля.
        </span>
      </div>
    </template>

    <a-form
      ref="formRef"
      class="login-modal__form stc-ant-field-skin"
      :class="{ 'login-modal__form--recover': isRecoverMode }"
      layout="vertical"
      :required-mark="false"
      :model="form"
      :rules="rules"
      @finish="onSubmit"
    >
      <a-form-item v-if="isLoginMode" name="login" label="Логин" class="stc-ant-field-skin">
        <a-input
          v-model:value="form.login"
          size="large"
          placeholder="Логин"
          autocomplete="username"
        />
      </a-form-item>

      <a-form-item v-if="isRecoverMode" name="email" label="Почта" class="stc-ant-field-skin">
        <a-input
          v-model:value="form.email"
          size="large"
          type="email"
          placeholder="Введите почту"
          autocomplete="email"
        />
      </a-form-item>

      <div v-if="isRecoverMode && recoverErrorMessage" class="login-modal__recover-error">
        <span class="login-modal__recover-error-icon" aria-hidden="true">
          <svg
            class="login-modal__recover-error-mark"
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="7" cy="7" r="7" fill="var(--stc-red-600)" />
            <path
              d="M4.25 4.25 L9.75 9.75 M9.75 4.25 L4.25 9.75"
              stroke="#fff"
              stroke-width="1.35"
              stroke-linecap="round"
            />
          </svg>
        </span>
        <span>{{ recoverErrorMessage }}</span>
      </div>

      <a-form-item v-if="isLoginMode" name="password" label="Пароль" class="stc-ant-field-skin">
        <a-input-password
          v-model:value="form.password"
          size="large"
          placeholder="Введите пароль"
          autocomplete="current-password"
          :icon-render="renderPasswordIcon"
        />
      </a-form-item>

      <a-alert
        v-if="isLoginMode && loginErrorMessage"
        class="login-modal__auth-error"
        type="error"
        :message="loginErrorMessage"
        show-icon
      />

      <div v-if="isLoginMode" class="login-modal__options">
        <a-checkbox v-model:checked="form.rememberMe">Запомнить меня</a-checkbox>

        <div class="login-modal__recover">
          <botton @click.prevent="switchToRecoverMode">Забыли пароль?</botton>
        </div>
      </div>

      <button
        v-if="isRecoverMode"
        type="button"
        class="login-modal__back"
        @click="switchToLoginMode"
      >
        Вернуться ко входу
      </button>

      <button
        type="submit"
        class="btn btn-primary btn-medium login-modal__footer"
        :disabled="!canSubmit || submitLoading"
      >
        {{ submitButtonText }}
      </button>
    </a-form>
  </a-modal>

  <ConsultationRequestSuccessModal
    :open="recoverSuccessModalOpen"
    title="Заявка отправлена"
    :description="recoverSuccessDescription"
    @update:open="onRecoverSuccessModalOpenUpdate"
  />
</template>

<script setup lang="ts">
import { computed, nextTick, reactive, ref, watch, h } from 'vue';
import { message } from 'ant-design-vue';
import type { FormInstance, Rule } from 'ant-design-vue/es/form';

import ConsultationRequestSuccessModal from '@/js/components/consultation-request-success-modal/consultation-request-success-modal.vue';
import { getApiUrl } from '@/js/utils/getApiUrl';
import { trackYaGoal } from '@/js/utils/yandex-metrika';

const LOGIN_ENDPOINT = '/api/auth/login/';
const RESET_PASSWORD_ENDPOINT = '/api/auth/reset-password/';
const ICONS_BASE =
  process.env.NODE_ENV === 'development'
    ? '/assets/images/icons'
    : '/local/templates/dist/assets/images/icons';

const isOpen = ref(false);
const mode = ref<'login' | 'recover'>('login');
const submitLoading = ref(false);
const loginErrorMessage = ref('');
const recoverErrorMessage = ref('');
const recoverSuccessModalOpen = ref(false);
const recoverSuccessDescription = ref('');
const formRef = ref<FormInstance>();

const RECOVER_SUCCESS_FALLBACK_DESCRIPTION =
  'Если указанный email зарегистрирован, мы отправили ссылку для смены пароля';
const LOGIN_BLOCK_ERROR_FALLBACK =
  'Ваш аккаунт временно заблокирован на 15 минут минут из-за превышения количества неверных авторизаций.';
const form = reactive({
  password: '',
  login: '',
  email: '',
  rememberMe: false,
});

const authValidator = async () => {
  if (mode.value === 'login' && loginErrorMessage.value) {
    return Promise.reject();
  }
  if (mode.value === 'recover' && recoverErrorMessage.value) {
    return Promise.reject();
  }
  return Promise.resolve();
};

const rules = computed<Record<string, Rule[]>>(() => {
  if (mode.value === 'login') {
    return {
      login: [{ required: true, message: 'Введите логин' }, { validator: authValidator }],
      password: [{ required: true, message: 'Введите пароль' }, { validator: authValidator }],
    } as Record<string, Rule[]>;
  }
  return {
    email: [
      { required: true, message: 'Введите почту' },
      { type: 'email', message: 'Введите корректный email' },
      { validator: authValidator },
    ],
  } as Record<string, Rule[]>;
});

const isLoginMode = computed(() => mode.value === 'login');
const isRecoverMode = computed(() => mode.value === 'recover');
const modalTitle = computed(() => (isRecoverMode.value ? 'Восстановить пароль' : 'Войти'));
const submitButtonText = computed(() => (isRecoverMode.value ? 'Отправить ссылку' : 'Войти'));
const isValidEmail = (value: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

const canSubmit = computed(() =>
  isRecoverMode.value
    ? form.email.trim().length > 0 && isValidEmail(form.email) && !recoverErrorMessage.value
    : form.login.trim().length > 0 && form.password.trim().length > 0 && !loginErrorMessage.value,
);

const readApiErrorMessage = (data: unknown): string | null => {
  if (!data || typeof data !== 'object') {
    return null;
  }
  const record = data as Record<string, unknown>;
  const candidates = ['message', 'detail', 'error', 'msg'];
  for (const key of candidates) {
    const value = record[key] as { code?: Number; error?: string } | string;
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    } else if (
      typeof value !== 'string' &&
      key === 'msg' &&
      value?.code === 400 &&
      value?.error?.trim()
    ) {
      return value.error.trim();
    } else if (typeof value !== 'string' && key === 'msg' && value?.code === 423) {
      return LOGIN_BLOCK_ERROR_FALLBACK;
    }
  }
  if (Array.isArray(record.detail) && record.detail.every((item) => typeof item === 'string')) {
    return record.detail.filter(Boolean).join(' ');
  }
  return null;
};

const readResetPasswordSuccessMsg = (data: unknown): string => {
  if (!data || typeof data !== 'object') {
    return '';
  }
  const msg = (data as Record<string, unknown>).msg;
  return typeof msg === 'string' ? msg.trim() : '';
};

const parseResponseBody = async (response: Response): Promise<unknown> => {
  const text = await response.text();
  if (!text) {
    return {};
  }
  try {
    return JSON.parse(text);
  } catch {
    return {};
  }
};

const clearLoginError = () => {
  loginErrorMessage.value = '';
  formRef.value?.clearValidate();
};

const clearRecoverError = () => {
  recoverErrorMessage.value = '';
  formRef.value?.clearValidate();
};

const resetFormState = () => {
  form.password = '';
  form.login = '';
  form.email = '';
  form.rememberMe = false;
  submitLoading.value = false;
  nextTick(() => {
    formRef.value?.clearValidate();
  });
};

watch(
  () => [form.login, form.password],
  () => {
    clearLoginError();
  },
);

watch(
  () => form.email,
  () => {
    clearRecoverError();
  },
);

const onSubmit = async () => {
  clearLoginError();
  clearRecoverError();
  submitLoading.value = true;
  try {
    if (isRecoverMode.value) {
      const response = await fetch(getApiUrl(RESET_PASSWORD_ENDPOINT), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ email: form.email.trim() }),
      });

      const data = await parseResponseBody(response);

      if (!response.ok) {
        const fromBody = readApiErrorMessage(data);
        throw new Error(fromBody ?? `Ошибка отправки (${response.status})`);
      }

      recoverSuccessDescription.value =
        readResetPasswordSuccessMsg(data) || RECOVER_SUCCESS_FALLBACK_DESCRIPTION;
      close(true);
      recoverSuccessModalOpen.value = true;
      return;
    }

    const response = await fetch(getApiUrl(LOGIN_ENDPOINT), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        email: form.login.trim(),
        password: form.password,
        remember: form.rememberMe ? 'Y' : 'N',
      }),
    });

    const data = await parseResponseBody(response);

    if (!response.ok) {
      const fromBody = readApiErrorMessage(data);
      throw new Error(fromBody ?? `Ошибка входа (${response.status})`);
    }

    message.success('Вход выполнен');
    trackYaGoal('lk');
    close();
    const redirect = (data as { msg?: { redirectTo?: string } })?.msg?.redirectTo;
    if (redirect) {
      window.location.replace(redirect);
      return;
    }
    window.location.reload();
  } catch (err) {
    const fallback = isRecoverMode.value
      ? 'Не удалось отправить ссылку'
      : 'Не удалось выполнить вход';
    if (isRecoverMode.value) {
      recoverErrorMessage.value = err instanceof Error ? err.message : fallback;
      await formRef.value?.validate();
      return;
    }
    if (isLoginMode.value) {
      loginErrorMessage.value = err instanceof Error ? err.message : fallback;
      await formRef.value?.validate();
      return;
    }
  } finally {
    submitLoading.value = false;
  }
};

const switchToRecoverMode = () => {
  clearLoginError();
  clearRecoverError();
  mode.value = 'recover';
};

const switchToLoginMode = () => {
  clearLoginError();
  clearRecoverError();
  mode.value = 'login';
};

const open = () => {
  resetFormState();
  switchToLoginMode();
  isOpen.value = true;
};

const onRecoverSuccessModalOpenUpdate = (open: boolean) => {
  recoverSuccessModalOpen.value = open;
  if (!open) {
    switchToLoginMode();
    recoverSuccessDescription.value = '';
  }
};

const close = (notClearRecoverDescription?: boolean) => {
  clearLoginError();
  clearRecoverError();
  resetFormState();
  recoverSuccessModalOpen.value = false;
  if (!notClearRecoverDescription) {
    recoverSuccessDescription.value = '';
  }
  switchToLoginMode();
  isOpen.value = false;
};

const renderPasswordIcon = (visible: boolean) =>
  h('img', {
    src: visible ? `${ICONS_BASE}/share-icons/eye-slash.svg` : `${ICONS_BASE}/share-icons/eye.svg`,
    alt: '',
    width: 24,
    height: 24,
  });

defineExpose({ open, close });
</script>
